import React, { useState, useEffect, useCallback, useRef } from 'react';
import { db } from '../db';
import { MediaRenderer, MediaTypeBadge, detectMediaType } from './CosyflowEditorMedia';
import electronBridge from '../utils/electronBridge';
import { getAudioMimeFromBase64 } from '../utils/audioUtils';

// Check if a path is an Electron file path (Windows or Unix)
const isElectronFilePath = (url) => {
  if (!url) return false;
  return electronBridge.isElectron && (url.includes(':\\') || url.includes('://'));
};

// Normalize base64 data to ensure proper data URI prefix
export const normalizeMediaUrl = (url) => {
  if (!url) return url;

  // Handle Electron file paths - return as-is since they can be used directly
  if (isElectronFilePath(url)) {
    return url;
  }

  if (url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) {
    return url;
  }
  // Raw base64 without prefix - detect type and add appropriate prefix
  if (url.startsWith('/9j/') || url.startsWith('/9J/')) {
    return `data:image/jpeg;base64,${url}`;
  }
  if (url.startsWith('iVBOR')) {
    return `data:image/png;base64,${url}`;
  }
  if (url.startsWith('R0lGOD')) {
    return `data:image/gif;base64,${url}`;
  }
  if (url.startsWith('UklGR')) {
    return `data:image/webp;base64,${url}`;
  }
  if (url.startsWith('AAAA')) {
    return `data:video/mp4;base64,${url}`;
  }
  if (url.startsWith('SUQz') || url.startsWith('//u')) {
    return `data:${getAudioMimeFromBase64(url)};base64,${url}`;
  }
  if (url.startsWith('Z2xURg')) {
    return `data:model/gltf-binary;base64,${url}`;
  }
  // Default to image/png for unknown base64
  return `data:image/png;base64,${url}`;
};

// Hook to load gallery images with optional model and dataType filtering
export const useModelGallery = (modelName, limit = 100, filterByModel = true, dataType = null) => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadImages = useCallback(async () => {
    if (filterByModel && !modelName) {
      setGalleryImages([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      let images;

      if (filterByModel && modelName) {
        images = await db.images
          .where('model')
          .equals(modelName)
          .reverse()
          .limit(limit)
          .toArray();
      } else if (dataType) {
        images = await db.images
          .where('dataType')
          .equals(dataType.toLowerCase())
          .reverse()
          .limit(limit)
          .toArray();
      } else {
        images = await db.images
          .orderBy('receivedtimestamp')
          .reverse()
          .limit(limit)
          .toArray();
      }

      const validImages = images
        .filter(img => img.image && img.image.length > 0)
        .map(img => ({ ...img, image: normalizeMediaUrl(img.image) }));

      setGalleryImages(validImages);
    } catch (err) {
      console.error('[ModelGalleryPicker] Error loading gallery images:', err);
      setError(err);
      setGalleryImages([]);
    } finally {
      setLoading(false);
    }
  }, [modelName, limit, filterByModel, dataType]);

  return { galleryImages, loading, error, loadImages, setGalleryImages };
};

const generateVideoThumbnail = (videoSrc, maxSize = 300, quality = 0.85) => {
    return new Promise((resolve) => {
        const video = document.createElement('video');
        if (videoSrc.startsWith('http://') || videoSrc.startsWith('https://')) {
            video.crossOrigin = 'anonymous';
        }
        video.muted = true;
        video.playsInline = true;

        video.onloadeddata = () => {
            video.currentTime = Math.min(1, video.duration / 2);
        };

        video.onseeked = () => {
            try {
                const canvas = document.createElement('canvas');
                const aspectRatio = video.videoWidth / video.videoHeight;

                if (aspectRatio > 1) {
                    canvas.width = maxSize;
                    canvas.height = Math.round(maxSize / aspectRatio);
                } else {
                    canvas.height = maxSize;
                    canvas.width = Math.round(maxSize * aspectRatio);
                }

                const ctx = canvas.getContext('2d');
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                const thumbnail = canvas.toDataURL('image/jpeg', quality);
                video.src = '';
                resolve(thumbnail);
            } catch (err) {
                resolve(null);
            }
        };

        video.onerror = () => {
            resolve(null);
        };

        setTimeout(() => resolve(null), 5000);

        video.src = videoSrc;
        video.load();
    });
};

const VideoThumbnail = ({ url, alt, className = '' }) => {
  const isDataUri = url && url.startsWith('data:');
  const [thumbnail, setThumbnail] = useState(null);
  const [loading, setLoading] = useState(isDataUri);
  const mountedRef = useRef(true);
  const videoRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    if (isDataUri) {
      setLoading(true);
      generateVideoThumbnail(url, 200, 0.7).then((thumb) => {
        if (mountedRef.current) {
          setThumbnail(thumb);
          setLoading(false);
        }
      });
    }
    return () => { mountedRef.current = false; };
  }, [url, isDataUri]);

  useEffect(() => {
    if (!isDataUri && videoRef.current) {
      const video = videoRef.current;
      const handleLoaded = () => {
        video.currentTime = Math.min(0.1, video.duration / 2);
      };
      video.addEventListener('loadeddata', handleLoaded);
      return () => video.removeEventListener('loadeddata', handleLoaded);
    }
  }, [isDataUri, url]);

  const playOverlay = (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div className="w-10 h-10 rounded-full bg-purple-500/70 flex items-center justify-center">
        <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M8 5v14l11-7z" />
        </svg>
      </div>
    </div>
  );

  if (isDataUri) {
    if (loading) {
      return (
        <div className={`${className} bg-dark-purple/60 flex items-center justify-center`}>
          <div className="w-6 h-6 border-2 border-white/20 border-t-purple-400 rounded-full animate-spin" />
        </div>
      );
    }
    if (thumbnail) {
      return (
        <div className={`relative ${className}`}>
          <img src={thumbnail} alt={alt} className="w-full h-full object-cover" loading="lazy" />
          {playOverlay}
        </div>
      );
    }
  }

  if (!isDataUri) {
    return (
      <div className={`relative ${className}`}>
        <video
          ref={videoRef}
          src={url}
          className="w-full h-full object-cover"
          muted
          playsInline
          preload="metadata"
        />
        {playOverlay}
      </div>
    );
  }

  return (
    <div className={`${className} bg-dark-purple/60 flex flex-col items-center justify-center`}>
      <div className="w-10 h-10 rounded-full bg-purple-500/30 flex items-center justify-center">
        <svg className="w-5 h-5 text-purple-300 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M8 5v14l11-7z" />
        </svg>
      </div>
      <span className="text-purple-300 text-xs mt-1">Video</span>
    </div>
  );
};

// Main Gallery Picker Component
const ModelGalleryPicker = ({
  isOpen,
  onClose,
  modelName,
  onSelect,
  selectedMedia = [],
  title = 'Select from Gallery',
  subtitle,
  multiSelect = true,
  filterByModel = true,
  dataType = null,
  limit = 100,
}) => {
  const { galleryImages, loading, loadImages } = useModelGallery(modelName, limit, filterByModel, dataType);

  useEffect(() => {
    if (isOpen && (modelName || !filterByModel)) {
      loadImages();
    }
  }, [isOpen, modelName, filterByModel, loadImages]);

  const handleSelect = (img) => {
    if (onSelect) {
      onSelect(img);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-dark-blue border-2 border-white/30 rounded-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-white/20">
          <div>
            <h3 className="text-xl font-bold text-white">{title}</h3>
            <p className="text-white/60 text-sm">
              {subtitle || (filterByModel ? `Media generated with: ${modelName}` : (dataType ? `All ${dataType} media` : 'All generated media'))}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin w-10 h-10 border-4 border-cyan border-t-transparent rounded-full" />
            </div>
          ) : galleryImages.length > 0 ? (
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {galleryImages.map((img, index) => {
                const isSelected = selectedMedia.includes(img.image) || (img.url && selectedMedia.includes(img.url));
                const mediaType = detectMediaType(img.image);
                return (
                  <div
                    key={img.id || index}
                    className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                      isSelected
                        ? 'border-green-500 ring-2 ring-green-500/50'
                        : 'border-white/20 hover:border-cyan/50'
                    }`}
                  >
                    {mediaType === 'video' ? (
                      <VideoThumbnail
                        url={img.image}
                        alt={`Gallery ${index + 1}`}
                        className="w-full h-full"
                      />
                    ) : (
                      <MediaRenderer
                        url={img.image}
                        alt={`Gallery ${index + 1}`}
                        className="w-full h-full"
                        mediaType={mediaType}
                      />
                    )}
                    {mediaType !== 'image' && (
                      <div className="absolute top-1 left-1 z-10">
                        <MediaTypeBadge type={mediaType} />
                      </div>
                    )}
                    {/* Clickable overlay for selection */}
                    <div
                      className={`absolute inset-0 z-20 flex items-center justify-center transition-all ${
                        isSelected ? 'bg-green-500/30' : 'hover:bg-white/10'
                      }`}
                      onClick={() => handleSelect(img)}
                    >
                      {isSelected && (
                        <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-20">
              <svg className="w-16 h-16 mx-auto text-white/30 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-white/60 text-lg font-medium">No media found</p>
              <p className="text-white/40 text-sm mt-2">Generate some content with this model first</p>
            </div>
          )}
        </div>
        <div className="p-4 border-t border-white/20 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModelGalleryPicker;
